import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Switch,
    Select,
    Badge,
    message,
    Popconfirm,
    Row,
    Col
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { settlementService } from '../../../api/enhancedApi';
import { formatDateTime } from '../../../utils/dateUtils';

const { Option } = Select;

const SalesRouteManagementPage = () => {
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    const [filterParams, setFilterParams] = useState({});
    const [form] = Form.useForm();
    const [filterForm] = Form.useForm();

    useEffect(() => {
        fetchRoutes();
    }, [filterParams]);

    /**
     * 提取错误信息的辅助函数
     * @param {Error} error - 错误对象
     * @param {string} defaultMessage - 默认错误信息
     * @returns {string} 错误信息
     */
    const extractErrorMessage = (error, defaultMessage = '操作失败') => {
        if (error.response && error.response.data) {
            if (typeof error.response.data === 'string') {
                return error.response.data;
            } else if (error.response.data.detail) {
                return error.response.data.detail;
            } else if (error.response.data.message) {
                return error.response.data.message;
            }
        } else if (error.message) {
            return error.message;
        }
        return defaultMessage;
    };

    /**
     * 根据航线构成自动生成航线代码
     * @param {Array} routeLegs - 航线构成数组 [{leg, dep, arr}, ...]
     * @returns {string} 生成的航线代码，如 "HKG-DWC-MAD-HKG"
     */
    const generateRouteCode = (routeLegs) => {
        if (!routeLegs || routeLegs.length === 0) {
            return '';
        }

        // 过滤出有效的航段（dep 和 arr 都不为空）
        const validLegs = routeLegs.filter(leg => leg.dep && leg.arr);
        if (validLegs.length === 0) {
            return '';
        }

        // 构建机场序列：第1段的DEP → 每段的ARR
        // 清理机场代码：去除空格、换行等（trim + 去除所有空白字符）
        const airports = [validLegs[0].dep.trim().replace(/\s+/g, '')];
        validLegs.forEach(leg => {
            airports.push(leg.arr.trim().replace(/\s+/g, ''));
        });

        // 用 "-" 连接生成航线代码
        return airports.join('-');
    };

    /**
     * 验证是否为标准闭合环线（首尾机场相同）。
     * 仅对 ≥2 段的航线做检查；单段航线（A-B）天然不闭合，
     * 不应弹出"非闭合环线"误导用户。
     * @param {Array} routeLegs - 航线构成数组
     * @returns {boolean} true 表示无需提示警告
     */
    const validateStandardRoute = (routeLegs) => {
        if (!routeLegs || routeLegs.length === 0) {
            return true;
        }

        const validLegs = routeLegs.filter(leg => leg.dep && leg.arr);
        if (validLegs.length <= 1) {
            return true;
        }

        const firstDep = validLegs[0].dep.trim();
        const lastArr = validLegs[validLegs.length - 1].arr.trim();

        return firstDep === lastArr;
    };

    /**
     * 处理航线构成变化，自动更新航线代码
     */
    const handleRouteLegsChange = (routeLegs) => {
        const routeCode = generateRouteCode(routeLegs);
        form.setFieldsValue({ route_code: routeCode });
    };

    const fetchRoutes = async () => {
        setLoading(true);
        try {
            const response = await settlementService.salesRoutes.getSalesRoutes(filterParams);
            // 修复：后端直接返回数组，不是 {data: {items: []}}
            setRoutes(response.data || []);
        } catch (error) {
            console.error('获取销售航线列表失败:', error);
            message.error(extractErrorMessage(error, '获取销售航线列表失败'));
        } finally {
            setLoading(false);
        }
    };

    // 处理搜索
    const handleSearch = (values) => {
        const params = {};
        
        if (values.route_code) {
            params.route_code = values.route_code;
        }
        
        if (values.name) {
            params.name = values.name;
        }
        
        if (values.status) {
            params.status = values.status;
        }
        
        setFilterParams(params);
    };

    // 重置搜索
    const handleReset = () => {
        filterForm.resetFields();
        setFilterParams({});
    };

    const handleCreate = () => {
        setEditingRoute(null);
        form.resetFields();
        // 默认带一段空航段，方便用户直接录入 A-B 而无需先点击"添加航段"
        form.setFieldsValue({ route_legs: [{ leg: 1, dep: '', arr: '' }] });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRoute(record);
        const routeLegs = JSON.parse(record.route_legs || '[]');
        form.setFieldsValue({
            ...record,
            route_legs: routeLegs,
            is_active: record.status === 'ACTIVE' // 将ACTIVE/INACTIVE转换为true/false
        });
        // 编辑时也自动生成一次航线代码（以防数据不一致）
        const routeCode = generateRouteCode(routeLegs);
        form.setFieldsValue({ route_code: routeCode });
        
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await settlementService.salesRoutes.deleteSalesRoute(id);
            message.success('删除成功');
            fetchRoutes();
        } catch (error) {
            console.error('删除失败:', error);
            message.error(extractErrorMessage(error, '删除失败'));
        }
    };

    const handleSubmit = async (values) => {
        try {
            const submitData = {
                ...values,
                route_legs: JSON.stringify(values.route_legs || []),
                status: values.is_active ? 'ACTIVE' : 'INACTIVE'
            };
            
            delete submitData.is_active;

            if (editingRoute) {
                await settlementService.salesRoutes.updateSalesRoute(editingRoute.id, submitData);
                message.success('更新成功');
            } else {
                await settlementService.salesRoutes.createSalesRoute(submitData);
                message.success('创建成功');
            }

            if (!validateStandardRoute(values.route_legs)) {
                message.warning('提示：该航线首尾机场不同，非闭合环线');
            }

            setModalVisible(false);
            form.resetFields();
            fetchRoutes();
        } catch (error) {
            console.error('操作失败:', error);
            message.error(extractErrorMessage(error, editingRoute ? '更新失败' : '创建失败'));
        }
    };

    const columns = [
        {
            title: '序号',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            align: 'center',
        },
        {
            title: '航线代码',
            dataIndex: 'route_code',
            key: 'route_code',
            width: 180,
            ellipsis: false, // 不换行
        },
        {
            title: '航线名称',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            ellipsis: true,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const isActive = status === 'ACTIVE';
                return <Badge className="status-badge" status={isActive ? "success" : "error"} text={isActive ? "启用" : "停用"} />;
            },
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            width: 250,
            ellipsis: true,
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (text) => formatDateTime(text),
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除这条销售航线吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const RouteLegsForm = ({ value = [], onChange }) => {
        const handleAdd = () => {
            const newLegs = [...value, { leg: value.length + 1, dep: '', arr: '' }];
            onChange(newLegs);
        };

        const handleRemove = (index) => {
            const newLegs = value.filter((_, i) => i !== index);
            onChange(newLegs);
        };

        const handleChange = (index, field, fieldValue) => {
            const newLegs = value.map((leg, i) =>
                i === index ? { ...leg, [field]: fieldValue } : leg
            );
            onChange(newLegs);
        };

        return (
            <div>
                {value.map((leg, index) => (
                    <Row key={index} gutter={16} style={{ marginBottom: 8 }}>
                        <Col span={2}>
                            <Input
                                value={leg.leg || index + 1}
                                placeholder="航段"
                                disabled
                            />
                        </Col>
                        <Col span={8}>
                            <Input
                                value={leg.dep}
                                placeholder="出发机场"
                                onChange={(e) => handleChange(index, 'dep', e.target.value.toUpperCase())}
                            />
                        </Col>
                        <Col span={8}>
                            <Input
                                value={leg.arr}
                                placeholder="到达机场"
                                onChange={(e) => handleChange(index, 'arr', e.target.value.toUpperCase())}
                            />
                        </Col>
                        <Col span={6}>
                            <Button
                                type="text"
                                danger
                                onClick={() => handleRemove(index)}
                                disabled={value.length <= 1}
                            >
                                删除
                            </Button>
                        </Col>
                    </Row>
                ))}
                <Button type="dashed" onClick={handleAdd} style={{ width: '100%' }}>
                    + 添加航段
                </Button>
            </div>
        );
    };

    return (
        <div className="page-container">
            {/* 页面标题区域 */}
            <div className="page-header-compact">
                <h3 className="page-title-compact">销售航线管理</h3>
                <div className="page-action-compact">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        新增销售航线
                    </Button>
                </div>
            </div>

            {/* 筛选区域 */}
            <Card className="filter-card">
                <Form 
                    form={filterForm} 
                    layout="horizontal" 
                    onFinish={handleSearch} 
                    className="filter-form" 
                    labelAlign="right"
                >
                    <Row gutter={24}>
                        <Col span={6}>
                            <Form.Item label="航线代码" name="route_code">
                                <Input placeholder="请输入航线代码" allowClear />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="航线名称" name="name">
                                <Input placeholder="请输入航线名称" allowClear />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="状态" name="status">
                                <Select placeholder="请选择状态" allowClear>
                                    <Option value="ACTIVE">启用</Option>
                                    <Option value="INACTIVE">停用</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={6} style={{ textAlign: 'right' }}>
                            <Form.Item>
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    icon={<SearchOutlined />} 
                                    style={{ marginRight: 8 }}
                                >
                                    搜索
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                    重置
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* 销售航线列表 */}
            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={routes}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    scroll={{ x: 1100 }}
                />
            </Card>

            {/* 创建/编辑弹窗 */}
            <Modal
                title={editingRoute ? '编辑销售航线' : '新增销售航线'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={(changedValues) => {
                        // 监听航线构成的变化，自动生成航线代码
                        if (changedValues.route_legs) {
                            handleRouteLegsChange(changedValues.route_legs);
                        }
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="route_code"
                                label="航线代码"
                                rules={[{ required: true, message: '请配置航线构成以自动生成航线代码' }]}
                                tooltip="根据航线构成自动生成，不可手动编辑"
                            >
                                <Input 
                                    placeholder="如：HKG-DWC-MAD-HKG" 
                                    disabled
                                    style={{ 
                                        backgroundColor: '#f5f5f5',
                                        color: '#000000d9',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="航线名称"
                                rules={[{ required: true, message: '请输入航线名称' }]}
                            >
                                <Input placeholder="如：杭州-欧洲全货运往返" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="route_legs"
                        label="航线构成"
                        rules={[{ required: true, message: '请配置航线构成' }]}
                    >
                        <RouteLegsForm />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label="状态"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="描述"
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="航线描述信息"
                        />
                    </Form.Item>

                    <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                        <Space>
                            <Button onClick={() => setModalVisible(false)}>
                                取消
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingRoute ? '更新' : '创建'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SalesRouteManagementPage;